import Stomp from 'stompjs'
import { settings } from '@/utils/settings'

const FTWebSocket = {
  websocketUrl: `${settings.API_URL}/websocket`,
  client: null,
  isConnected: false,
  isConnecting: false,
  subscriptions: [],
  connectAttempts: 0,
  maxConnectAttempts: 6
}

const getStore = () => {
  const storeModule = require('@/store')
  return storeModule.default || storeModule
}

const resolveSockJS = () => {
  try {
    // eslint-disable-next-line global-require
    const mod = require('sockjs-client')
    return mod && mod.default ? mod.default : mod
  } catch (err) {
    return null
  }
}

const resolveStompOver = () => {
  if (Stomp && typeof Stomp.over === 'function') {
    return Stomp.over.bind(Stomp)
  }
  if (Stomp && Stomp.default && typeof Stomp.default.over === 'function') {
    return Stomp.default.over.bind(Stomp.default)
  }
  if (Stomp && Stomp.Stomp && typeof Stomp.Stomp.over === 'function') {
    return Stomp.Stomp.over.bind(Stomp.Stomp)
  }
  return null
}

const connectSuccessHandler = (frame) => {
  FTWebSocket.isConnected = true
  FTWebSocket.isConnecting = false
  FTWebSocket.connectAttempts = 0

  console.log(
    `Successfully connected user ${frame.headers['user-name']} to websocket url ${FTWebSocket.websocketUrl}`
  )
  FTWebSocket.subscriptions.forEach((sub) => {
    FTWebSocket.subscribe(sub.destination, sub.callback)
  })
}

const connectErrorHandler = (e) => {
  if (typeof e === 'string' && e.indexOf('Lost connection') >= 0) {
    FTWebSocket.isConnecting = false
    FTWebSocket.isConnected = false

    if (FTWebSocket.connectAttempts < FTWebSocket.maxConnectAttempts) {
      const waitForRetrySeconds = 2 ** (FTWebSocket.connectAttempts + 1)
      console.log(
        `Failed/Lost connection to websocket url ${FTWebSocket.websocketUrl}. Attempting to automatically reconnect in ${waitForRetrySeconds} seconds`
      )
      setTimeout(FTWebSocket.connect, 1000 * waitForRetrySeconds)
    } else {
      console.log(
        `Unable to reconnect to websocket url ${FTWebSocket.websocketUrl} after ${FTWebSocket.maxConnectAttempts} attempts. Giving up!`
      )
    }
  } else {
    console.log(
      `Error connecting to websocket url ${FTWebSocket.websocketUrl}`,
      e
    )

    if (e?.headers?.message?.indexOf('JwtValidationException') >= 0) {
      console.log(
        'Detected connection failure is due to an expired JWT access token.  Disabling further reconnect attempts!'
      )
      FTWebSocket.connectAttempts = FTWebSocket.maxConnectAttempts + 1
    }
  }
}

FTWebSocket.connect = async () => {
  if (FTWebSocket.isConnected || FTWebSocket.isConnecting) {
    return FTWebSocket.client
  }

  FTWebSocket.isConnecting = true
  FTWebSocket.connectAttempts++

  console.log(
    `Connecting to websocket url ${FTWebSocket.websocketUrl} (attempt ${FTWebSocket.connectAttempts} of ${FTWebSocket.maxConnectAttempts})`
  )
  const SockJS = resolveSockJS()
  if (!SockJS) {
    FTWebSocket.isConnecting = false
    throw new Error("Cannot find module 'sockjs-client'")
  }
  const socket = new SockJS(FTWebSocket.websocketUrl)
  // socket.withCredentials = true
  const stompOver = resolveStompOver()
  if (!stompOver) {
    FTWebSocket.isConnecting = false
    throw new Error('stompjs over() is not available')
  }
  FTWebSocket.client = stompOver(socket)
  FTWebSocket.client.debug = undefined // Disables default debug logs

  FTWebSocket.client.connect(
    { Authorization: `Bearer ${getStore().state.auth?.accessToken}` },
    connectSuccessHandler,
    connectErrorHandler
  )
  return FTWebSocket.client
}

FTWebSocket.disconnect = () => {
  FTWebSocket.isConnecting = false
  FTWebSocket.isConnected = false
  FTWebSocket.connectAttempts = 0
  FTWebSocket.subscriptions = [] // Maintains a list of subscriptions so that if we need to re-establish a connection
  // to the backend we can re-subscribe previous subscriptions too!

  if (FTWebSocket.client) {
    FTWebSocket.client.disconnect()
    FTWebSocket.client = null
  }
}

FTWebSocket.subscribe = async (destination, callback) => {
  let subscription = FTWebSocket.subscriptions.find(
    (sub) => sub.destination === destination
  )
  if (!subscription) {
    subscription = { destination, callback }
    FTWebSocket.subscriptions.push(subscription)
  }
  await FTWebSocket.connect()
  if (!FTWebSocket.isConnected) {
    // If the client has not yet completed connection to the websocket we simply bail at this point.
    // Any subscriptions that were requested prior to the connection being established will be retried when the
    // client successfully connects.
    return
  }

  const { id } = await FTWebSocket.client.subscribe(destination, (message) => {
    try {
      callback(JSON.parse(message.body))
    } catch (err) {
      console.error(
        `Unable to parse websocket payload from ${destination}`,
        err
      )
    }
  })
  console.log(`Subscribing to websocket destination ${destination}, id ${id}`)

  subscription.id = id
  return id
}

FTWebSocket.unsubscribe = (id) => {
  console.log(`Unsubscribing to websocket id ${id}`)
  FTWebSocket.subscriptions = FTWebSocket.subscriptions.filter(
    (sub) => sub.id !== id
  )
  if (FTWebSocket.client) {
    FTWebSocket.client.unsubscribe(id)
  }
}

// Backward compatibility for existing callers using the misspelled method name.
FTWebSocket.unsubcribe = FTWebSocket.unsubscribe

export { FTWebSocket }
